import "./ClientInfo.scss";
import useResponsive from "../../../hooks/useResponsive";
import { LogoSection } from "../../../Components/LogoSection";
import { OrderSummary } from "../../../Components/OrderSummary";
import { Subtitle, Title, Subheader } from "../../../Components/Typography";
import { Input } from "../../../Components/Input";

import { useContext, useEffect, useState } from "react";
import {
  PaymentContext,
  PaymentContextProps,
} from "../../../Contexts/PaymentContext";
import { routes } from "../../../Router";
import { useNavigate, useSearchParams } from "react-router-dom";
import useDatabase from "../../../hooks/useDatabase";
import {
  AddressContext,
  AddressContextProps,
} from "../../../Contexts/AddressContext";
import {
  OrderContext,
  OrderContextProps,
} from "../../../Contexts/OrderContext";
import AddressTypeEnum from "../../../types/AddressTypeEnum";
import useLocalStorage from "../../../hooks/useLocalStorage";
import {
  EntitiesContext,
  EntitiesContextProps,
} from "../../../Contexts/EntitiesContext";
import {
  FileUploadContext,
  FileUploadContextProps,
} from "../../../Contexts/FileUploadContext";
import { toast } from "react-toastify";
import useApi from '../../../hooks/useApi';
import { initMercadoPago } from '@mercadopago/sdk-react';
import { PaymentStatusEnum } from "../../../types/PaymentStatusEnum";

export const ClientInfo = () => {
	const navigate = useNavigate();
  const { isDesktopOrLaptop } = useResponsive();
  const { addCollection, bulkAddCollection } = useDatabase();
  const { storageKeys, getSessionStorageItem } = useLocalStorage();
  const [loading, setLoading] = useState(false)

  const trackingCode = getSessionStorageItem(storageKeys.randomId);

	const { paymentForm, preferenceResponse }: PaymentContextProps = useContext<any>(PaymentContext);
	const {
    name,
    setName,
    dni,
    setDni,
    email,
    setEmail,
    phoneNumber,
    setPhoneNumber,
    paymentErrors,
    validatePaymentForm,
  } = paymentForm;
	const { preferenceId, setPreferenceId } = preferenceResponse;

	const { addressType, addressSend, addressPickup }: AddressContextProps = useContext<any>(AddressContext);

const {
	sheetType,
	sheetSize,
	color,
	ringType,
	side,
	copies,
  shippingPrice,
	totalPrice,
}: OrderContextProps = useContext<any>(OrderContext);

const { entities, refreshEntity }: EntitiesContextProps =
	useContext<any>(EntitiesContext);

const { fileUpload, additionalNotes }: FileUploadContextProps =
	useContext<any>(FileUploadContext);

const { createMPPreference } = useApi();
const [searchParams] = useSearchParams();

const api_key_mp = process.env.REACT_APP_MP_PUBLIC_KEY;

if (api_key_mp) {
  initMercadoPago(api_key_mp, { locale: 'es-AR' });
} else {
  console.error('Mercado Pago API key is not defined');
}

useEffect(() => {
  refreshEntity("status");
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

useEffect(() => {
  const status = searchParams.get('status');

  if (status) {
    if (status === 'approved') {
      navigate(routes.clientOrderConfirm.path);
    } else {
      toast.error('Error al procesar el pago');
      navigate(routes.clientNewOrder.path);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

const onClickNext = async () => {
  try {
    if (validatePaymentForm()) {
      setLoading(true)
      const userData = {
        ...paymentForm,
      };

      if (userData.paymentErrors) delete userData.paymentErrors;

      const newOrder = {
        id_color: color?.selectedColor?.value,
        id_faz: side?.selectedSide?.value,
        id_hoja: sheetSize?.selectedSheetSize?.value,
        id_papel: sheetType?.selectedSheetType?.value,
        id_copias: null,
        id_anillado: ringType?.selectedRingType?.value,
        copies: copies.numberOfCopies,
        id_sucursal: 
          addressType.selectedAddressType === AddressTypeEnum.PICKUP
            ? addressPickup.selectedBranch?.id
            : null,
        notes: additionalNotes,
        address_send: 
          addressType.selectedAddressType === AddressTypeEnum.SEND
            ? JSON.stringify(addressSend.addressSendForm)
            : null,
        user_data: JSON.stringify(userData) ?? null,
        tracking_code: trackingCode,
        shipping_value: shippingPrice,
        total_value: totalPrice,
        qty_files: fileUpload.uploadedFiles.length,
        qty_pages: fileUpload.uploadedPages,
        id_status: entities.status[0].id, // pendiente
        payment_status: PaymentStatusEnum.PENDIENTE
      };

      const createdItem = await addCollection("order", newOrder);

      if (createdItem) {
        const uploadItems = await bulkAddCollection(
          'upload',
          fileUpload.uploadedFiles.map((file) => ({
            id_order: createdItem.id,
            name: file.name,
            size: file.size,
            qty_pages: file.pdfPages,
            url: file.downloadUrl
          }))
        );

        if (uploadItems) {
          const preferenceOptions = {
            title: `Orden: ${createdItem.tracking_code} - Nombre: ${userData.name} - Cantidad de archivos: ${fileUpload.uploadedFiles.length} - Páginas: ${fileUpload.uploadedPages}`,
            quantity: 1,
            shippingPrice: shippingPrice,
            unitPrice: totalPrice,
            currency_id: 'ARS',
            trackingCode: createdItem.tracking_code,
            dni: userData.dni,
            name: userData.name,
            email: userData.email
          };

          const result = await createMPPreference(preferenceOptions);
          setLoading(false)

          if (result?.preference_id) {
            setPreferenceId(result?.preference_id ?? '');
            navigate(routes.clientPayment.path)
          }
        } else {
          setLoading(false)
          toast.error('Error al crear order upload')  
        }
      } else {
        setLoading(false)
        toast.error('Error al crear order')  
      }
    } else {
      // [Mobile] scroll to top
      window.scrollTo(0, 0);
      toast.error('Formulario no valido')
    }
  } catch(ex) {
    console.log(ex)
    toast.error('Error al crear order')
  }
};

const onClickBack = () => {
  navigate(routes.clientAddress.path);
};

return (
  <div className={isDesktopOrLaptop ? "client-info-desktop" : "client-info-mobile"}>
    <LogoSection />

    <div className="main-column">
      <div className="top-content">
        <div className="next-steps-section">
          <div className="step"></div>

          <div className="step">
            <Subheader disabled>Paso 1</Subheader>
            <Subtitle disabled>Archivos y Opciones de Impresión</Subtitle>
          </div>

          <div className="step">
            <Subheader disabled>Paso 2</Subheader>
            <Subtitle disabled>Opciones de Envío</Subtitle>
          </div>
        </div>
        <div className="title-section">
          <Subheader>Paso 3</Subheader>

          <Title>Datos para retirar la orden</Title>
        </div>

        {isDesktopOrLaptop ? (
          <div className="form-section">
            <div className="flex-row">
              <Input
                title="Nombre completo"
                value={name}
                onChange={setName}
                error={paymentErrors?.name}
              />
              <Input
                title="DNI / CUIT"
                value={dni}
                onChange={setDni}
                length={11}
                error={paymentErrors?.dni}
                mask={Number}
              />
            </div>
            <div className="flex-row">
              <Input
                title="Email"
                value={email}
                onChange={setEmail}
                error={paymentErrors?.email}
              />

              <Input
                phoneNumber
                title="Teléfono - Código de Area + Número (sin el 15)"
                value={phoneNumber}
                onChange={setPhoneNumber}
                error={paymentErrors?.phoneNumber}
                length={15}
              />
            </div>
          </div>
        ) : (
          <div className="form-section">
            <Input
              title="Nombre completo"
              value={name}
              onChange={setName}
              error={paymentErrors?.name}
            />
            <Input
              title="DNI / CUIT"
              value={dni}
              onChange={setDni}
              length={11}
              error={paymentErrors?.dni}
              mask={Number}
            />
            <Input
              title="Email"
              value={email}
              onChange={setEmail}
              error={paymentErrors?.email}
            />
            <Input
              phoneNumber
              title="Teléfono - Código de Area + Número (sin el 15)"
              value={phoneNumber}
              onChange={setPhoneNumber}
              error={paymentErrors?.phoneNumber}
              length={15}
            />
          </div>
        )}
       
      </div>

      <div className="bottom-content">
          <div className="next-steps-section">
            <div className="step"></div>

            <div className="step">
              <Subheader disabled>Paso 4</Subheader>
              <Subtitle disabled>Confirmar y pagar la orden</Subtitle>
            </div>
          </div>
        </div>
    </div>

    <OrderSummary
      showAddressForm
      showPaymentForm
      isNextEnabled
      onClickNext={onClickNext}
      onClickBack={onClickBack}
      loadingNext={loading}
      preferenceId={preferenceId}
    />
  </div>
  );
};
